<template>
  <div class="nurse-management">
    <div class="container-fluid bg-white rounded shadow p-4">
    <div class="header-section">
        <h1>Nurse Management</h1>
        <div class="action-buttons">
          <button
            v-if="$canAccess('nurse-add')"
            class="btn btn-primary" 
            @click="$router.push({ name: 'nurse-add' })"
          >
            <i class="fas fa-plus-circle"></i> Add Nurse
          </button>

          <button
              class="btn btn-info ms-2"
              @click="$router.push({ name: 'DepartmentsList' })"
          >
            <i class="fas fa-hospital"></i> Departments
          </button>
          <button
              v-if="$canAccess('SpecializationList')"
              class="btn btn-warning ms-2"
              @click="$router.push({ name: 'SpecializationList' })"
          >
            <i class="fas fa-certificate"></i> Specializations
          </button>
          <button
              v-if="$canAccess('ShiftList')"
              class="btn btn-success ms-2"
              @click="$router.push({ name: 'ShiftList' })"
          >
            <i class="fas fa-clock"></i> Shifts
          </button>
          <button
              v-if="$canAccess('PatientActivityLevelList')"
              class="btn btn-dark ms-2"
              @click="$router.push({ name: 'PatientActivityLevelList' })"
          >
            <i class="fas fa-walking"></i> Activity Levels
          </button>

          <button
              v-if="$canAccess('StaffingRequirementList')"
              class="btn btn-info ms-2"
              @click="$router.push({ name: 'StaffingRequirementList' })"
          >
            <i class="fas fa-users"></i> Staffing Requirements
          </button>

          <button
              v-if="$canAccess('NurseSchedule')"
              class="btn btn-primary ms-2"
              @click="$router.push({ name: 'NurseSchedule' })"
          >
            <i class="fas fa-calendar"></i> Nurse Schedule
          </button>

          <button
              class="btn btn-secondary ms-2"
              @click="$router.push({ name: 'Dashboard' })"
          >
            <i class="fas fa-arrow-left"></i> Back to Dashboard
          </button>



        </div>
      </div>

      <!-- Loading Indicator -->
      <div v-if="loading" class="loading-container">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Error Alert -->
      <div v-if="error" class="alert alert-danger">
        {{ error }}
      </div>

      <!-- Nurses Table -->
      <div v-if="!loading && nurses && nurses.length > 0" class="nurse-table-container w-100">
        <div class="table-filter mb-3 d-flex gap-3 align-items-center">
          <div class="filter-item">
            <input
                type="text"
                class="form-control"
                v-model="searchTerm"
                placeholder="Search nurses..."
                style="min-width: 200px;"
            >
          </div>
          <div class="filter-item">
            <select class="form-select" v-model="statusFilter" style="min-width: 150px;">
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <table class="table table-striped table-hover table-bordered w-100 text-start">
        <thead>
            <tr>
              <th @click="sortBy('name')">
                Name 
                <i class="fas fa-sort"></i>
              </th>
              <th @click="sortBy('email')">
                Email
                <i class="fas fa-sort"></i>
              </th>
              <!-- Dynamic specialization columns -->
<!--              <th v-for="specialization in uniqueSpecializations" :key="specialization.id">-->
<!--                {{ specialization.name }}-->
<!--              </th>-->
              <th>Specialisations</th>
              <th @click="sortBy('department')">
                Department
                <i class="fas fa-sort"></i>
              </th>
              <th @click="sortBy('joining_date')">
                Joined
                <i class="fas fa-sort"></i>
              </th>
              <th @click="sortBy('status')">
                Status
                <i class="fas fa-sort"></i>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="nurse in paginatedNurses" :key="nurse.id" :class="{'inactive-row': !nurse.is_active}">
              <td>{{ nurse.name }}</td>
              <td>{{ nurse.email }}</td>
              <!-- Dynamic specialization cells with checkmarks -->
              <td>
                {{ formatSpecializations(nurse.specializations) }}
              </td>
              <td>{{ nurse.department || 'Not Assigned' }}</td>
              <td>{{ formatDate(nurse.joining_date) }}</td>
              <td>
                <span :class="nurse.is_active ? 'badge bg-success' : 'badge bg-danger'">
                  {{ nurse.is_active ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td>
                <div class="btn-group">
                  <button
                    v-if="$canAccess('nurse-edit')"
                    class="btn btn-sm btn-outline-primary"
                    @click="editNurse(nurse.id)"
                  >
                    <i class="fas fa-edit">Edit</i>
                  </button>
                  <button
                    v-if="$canAccess('nurse-toggle')"
                    class="btn btn-sm"
                    :class="nurse.is_active ? 'btn-outline-danger' : 'btn-outline-success'"
                    @click="toggleStatus(nurse.id)"
                  >
                    <i :class="nurse.is_active ? 'fas fa-user-slash' : 'fas fa-user-check'"></i>
                  </button>
                  <button
                    v-if="$canAccess('nurse-view')"
                    class="btn btn-sm btn-outline-info"
                    @click="viewNurseDetails(nurse.id)"
                  >
                    <i class="fas fa-eye"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div class="pagination-container">
          <nav>
            <ul class="pagination">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <button class="page-link" @click="setPage(currentPage - 1)">Previous</button>
              </li>
              <li
                v-for="page in totalPages"
                :key="page"
                class="page-item"
                :class="{ active: page === currentPage }"
              >
                <button class="page-link" @click="setPage(page)">{{ page }}</button>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <button class="page-link" @click="setPage(currentPage + 1)">Next</button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!loading && (!nurses || nurses.length === 0)" class="empty-state">
        <div class="empty-state-content">
          <i class="fas fa-user-nurse fa-3x"></i>
          <h3>No Nurses Found</h3>
          <p>There are no nurses in the system yet. Get started by adding a new nurse.</p>
          <button
            v-if="$canAccess('nurse-add')"
            class="btn btn-primary"
            @click="$router.push({ name: 'nurse-add' })"
          >
            <i class="fas fa-plus-circle"></i> Add Nurse
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NurseList',
  data() {
    return {
      nurses: [], // Initialize as empty array to prevent undefined
      loading: false,
      error: null,
      searchTerm: '',
      statusFilter: 'all',
      sortColumn: 'name',
      sortDirection: 'asc',
      currentPage: 1,
      itemsPerPage: 10,
      allSpecializations: [] // Store all specializations
    }
  },
  computed: {
    filteredNurses() {
      // Add a null check here
      if (!this.nurses) return [];

      let filtered = [...this.nurses];

      // Apply search filter
      if (this.searchTerm) {
        const searchLower = this.searchTerm.toLowerCase();
        filtered = filtered.filter(nurse =>
          nurse.name.toLowerCase().includes(searchLower) ||
          nurse.email.toLowerCase().includes(searchLower) ||
          (nurse.department && nurse.department.toLowerCase().includes(searchLower))
        );
      }

      // Apply status filter
      if (this.statusFilter !== 'all') {
        const isActive = this.statusFilter === 'active';
        filtered = filtered.filter(nurse => nurse.is_active === isActive);
      }

      // Apply sorting
      filtered.sort((a, b) => {
        let valueA = a[this.sortColumn];
        let valueB = b[this.sortColumn];

        // Handle null values
        if (valueA === null) valueA = '';
        if (valueB === null) valueB = '';

        // String comparison
        if (typeof valueA === 'string') {
          valueA = valueA.toLowerCase();
          valueB = valueB.toLowerCase();
        }

        // Date comparison
        if (this.sortColumn === 'joining_date') {
          valueA = new Date(valueA);
          valueB = new Date(valueB);
        }

        // Do the actual comparison
        if (valueA < valueB) return this.sortDirection === 'asc' ? -1 : 1;
        if (valueA > valueB) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });

      return filtered;
    },
    paginatedNurses() {
      const startIndex = (this.currentPage - 1) * this.itemsPerPage;
      const endIndex = startIndex + this.itemsPerPage;
      return this.filteredNurses.slice(startIndex, endIndex);
    },
    totalPages() {
      return Math.ceil(this.filteredNurses.length / this.itemsPerPage);
    },
    uniqueSpecializations() {
      // Extract all unique specializations from all nurses
      const specializationsMap = new Map();

      // Process all specializations from all nurses
      this.nurses.forEach(nurse => {
        if (nurse.specializations && Array.isArray(nurse.specializations)) {
          nurse.specializations.forEach(spec => {
            if (spec && spec.id && !specializationsMap.has(spec.id)) {
              specializationsMap.set(spec.id, spec);
            }
          });
        }
      });

      // Convert map to array and sort alphabetically by name
      const result = Array.from(specializationsMap.values());
      // console.log('Unique specializations:', result);
      return result.sort((a, b) => a.name.localeCompare(b.name));
    }
  },
  methods: {
    hasSpecialization(nurse, specializationId) {
      if (!nurse || !nurse.specializations || !Array.isArray(nurse.specializations)) {
        return false;
      }


      // Check if the nurse has a specialization with the given ID
      const hasSpecialization = nurse.specializations.some(spec => spec && spec.id === specializationId);
      return hasSpecialization;
    },

    formatSpecializations(specializations) {
      if (!specializations || !Array.isArray(specializations) || specializations.length === 0) {
        return 'None';
      }

      // Get all specialization names and join them with commas
      return specializations
        .filter(spec => spec && spec.name)
        .map(spec => spec.name)
        .join(', ');
    },
    async fetchNurses() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await this.$api.get('/nurses');
        this.nurses = response.data.map(nurse => ({
          id: nurse.id,
          name: nurse.user.name,
          email: nurse.user.email,
          specializations: nurse.specializations || [],
          department: nurse.department?.name,
          joining_date: nurse.joining_date,
          is_active: nurse.user.is_active
        }));
        console.log('Fetched nurses:', this.nurses);
      } catch (error) {
        console.error('Error fetching nurses:', error);
        this.error = 'Failed to load nurses. Please try again later.';
        this.nurses = []; // Initialize as empty array on error
      } finally {
        this.loading = false;
      }
    },
    sortBy(column) {
      if (this.sortColumn === column) {
        // Toggle direction if already sorting by this column
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        // Default to ascending for new column
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
    },
    setPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
    async toggleStatus(nurseId) {
      if (!confirm('Are you sure you want to change this nurse\'s status?')) return;
      
      try {
        await this.$api.patch(`/nurses/${nurseId}/toggle`);
        const nurse = this.nurses.find(n => n.id === nurseId);
        if (nurse) {
          nurse.is_active = !nurse.is_active;
          this.$toast.success(`Nurse ${nurse.is_active ? 'activated' : 'deactivated'} successfully`);
        }
      } catch (error) {
        console.error('Error toggling nurse status:', error);
        this.$toast.error('Failed to update nurse status. Please try again.');
      }
    },
    editNurse(nurseId) {
      this.$router.push({ 
        name: 'nurse-edit', 
        params: { id: nurseId } 
      });
    },
    viewNurseDetails(nurseId) {
      this.$router.push({ 
        name: 'nurse-view', 
        params: { id: nurseId } 
      });
    }
  },
  mounted() {
    this.fetchNurses();
  }
}
</script>